import type { TExposedBankDTO } from '@doutorsim/shared/exposed/banks'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'
import type { FieldValues, Path, UseFormResetField } from 'react-hook-form'

import { Button } from '@/_components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/_components/ui/command'
import { FormControl } from '@/_components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/_components/ui/popover'
import { cn } from '@/_lib/utils'
import { queries } from '@/features/_lib/react-query'

interface BankSelectProps<T extends FieldValues = FieldValues> {
  selectedBankCode?: string | null | undefined
  onChange: (value: string) => void
  resetField?: UseFormResetField<T>
  disabled?: boolean
}

export function BankSelect<T extends FieldValues = FieldValues>({
  selectedBankCode,
  onChange,
  resetField,
  disabled,
}: BankSelectProps<T>) {
  const [open, setOpen] = useState(false)

  const { data: banks = [] } = useQuery(queries.banks.list)

  const handleSelectBank = (bank: TExposedBankDTO) => {
    const isAlreadySelected = bank.bankCode === selectedBankCode

    if (isAlreadySelected) {
      if (resetField) {
        resetField('bank' as Path<T>, { defaultValue: undefined })
      }
      return setOpen(false)
    }

    onChange(bank.bankCode)
    setOpen(false)
  }

  const selectedBank = banks.find((bank) => selectedBankCode === bank.bankCode)

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <FormControl>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={`w-full justify-between hover:bg-transparent hover:text-brand-grayscale-12 disabled:pointer-events-none disabled:bg-brand-grayscale-5`}
            disabled={disabled}
          >
            {selectedBankCode ? (
              <>
                <span className="truncate">
                  {`${selectedBank?.bankCode} - ${selectedBank?.name}`}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </>
            ) : (
              <>
                Selecione o banco
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </>
            )}
          </Button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent className="w-[293px] p-0">
        <Command>
          <CommandInput placeholder="Selecione o banco" />
          <CommandEmpty>Nenhuma banco encontrado.</CommandEmpty>
          <CommandGroup className="scrollbar max-h-60 overflow-y-auto">
            {banks.map((bank) => (
              <CommandItem
                key={bank.bankCode}
                value={bank.name}
                onSelect={() => handleSelectBank(bank)}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    selectedBank?.bankCode === bank.bankCode ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {`${bank.bankCode} - ${bank.name}`}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
